import { useQuery } from '@tanstack/react-query'
import type { LineColumnDef } from '@project/contracts'
import { useApi, type LineRow } from '../app/api.js'
import { asText, formatRate } from './format.js'

/** One of the company's GST slabs: "GST @ 18%" at 18. */
export interface GstSlab {
  readonly id: string
  readonly label: string
  readonly rate: string
}

/**
 * The company's GST slabs, for a `taxSlab` cell (INV-001, PROJ1-039).
 *
 * Read through the tax lookup, which lists only active GST records — never a
 * CGST or SGST half, which is not a line's tax. Fetched once per company and
 * only when a grid on screen has such a column.
 */
export const useGstSlabs = (enabled: boolean): readonly GstSlab[] => {
  const api = useApi()
  const q = useQuery({
    queryKey: ['gstSlabs', api.companyId],
    queryFn: () => api.lookup('tax_rate', ''),
    enabled,
    staleTime: 10 * 60_000,
  })
  return (q.data ?? [])
    .map((o) => ({ id: o.id, label: o.label, rate: asText(o.extra?.['ratePct']) }))
    .filter((s) => s.rate !== '')
}

const sameRate = (a: string, b: string): boolean => a !== '' && b !== '' && Number(a) === Number(b)

/** The slab a row's tax is: the one it picked, else the one its rate matches. */
export const slabOf = (slabs: readonly GstSlab[], row: LineRow, column: LineColumnDef): GstSlab | undefined => {
  const picked = asText(row['taxRateId'])
  const rate = asText(row[column.key])
  return (picked ? slabs.find((s) => s.id === picked) : undefined) ?? slabs.find((s) => sameRate(s.rate, rate))
}

/** What a `taxSlab` cell reads as: the slab's name, or the bare rate when no slab matches. */
export const slabText = (slabs: readonly GstSlab[], row: LineRow, column: LineColumnDef): string => {
  const slab = slabOf(slabs, row, column)
  if (slab) return slab.label
  const rate = asText(row[column.key])
  return rate === '' ? '' : `${formatRate(rate)}%`
}

/** The cells a slab pick writes: the rate every total reads, and the pick itself. */
export const slabPatch = (slabs: readonly GstSlab[], column: LineColumnDef, id: string): Record<string, string> => {
  const slab = slabs.find((s) => s.id === id)
  return slab ? { taxRateId: slab.id, [column.key]: slab.rate } : { taxRateId: '' }
}
